import { NONE_SELECTED } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import { DatasetOrigin } from '@/shared/domain/taskTypes';

export const HUMAN_LABEL_CONFIDENCE_TOOLTIP = '100% confidence by human annotation';

export function confidenceToPercent(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const pct = n <= 1 ? Math.round(n * 100) : Math.round(n);
    return Math.min(100, Math.max(0, pct));
}

/**
 * Raw pre-calibration distribution built from the labeling model's logprobs or
 * prompted probabilities. `record.probs` and `response.*_confidence` hold
 * post-calibration values that collapse to 1.0 when no calibration is fit,
 * so they must not be used here. A uniform distribution is the backend's
 * "model returned no probabilities" placeholder and counts as missing data.
 */
function readOriginalProbs(record) {
    const probs = record?.original_probs;
    if (!Array.isArray(probs) || probs.length === 0) return null;
    const nums = probs.map(Number);
    if (nums.some((n) => !Number.isFinite(n))) return null;
    const max = Math.max(...nums);
    const min = Math.min(...nums);
    const isUniformPlaceholder = nums.length > 1 && max - min < 1e-9;
    return isUniformPlaceholder ? null : nums;
}

function tokenLogprobValue(entry) {
    if (typeof entry === 'number') return entry;
    if (entry && Number.isFinite(Number(entry.logprob))) return Number(entry.logprob);
    return null;
}

/**
 * Mean token probability from persisted per-token logprobs.
 * Matches gateway `confidence_from_logprobs` (`mean(exp(logprob))`).
 */
function confidenceFromLogprobs(logprobs) {
    if (!Array.isArray(logprobs) || logprobs.length === 0) return null;
    const tokenProbs = [];
    for (const entry of logprobs) {
        const lp = tokenLogprobValue(entry);
        if (lp == null || !Number.isFinite(lp)) continue;
        tokenProbs.push(Math.exp(lp));
    }
    if (tokenProbs.length === 0) return null;
    return tokenProbs.reduce((sum, p) => sum + p, 0) / tokenProbs.length;
}

function isFullyGeneratedRecord(record) {
    return (
        record?.response?.response_origin === DatasetOrigin.specific_ai_fully_generated
        || record?.data_creation_type === DatasetOrigin.specific_ai_fully_generated
    );
}

function readUncalibratedConfidence(record) {
    const value = record?.response?.uncalibrated_confidence;
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function readOriginalProbForLabel(record, labelName, allLabelOptions) {
    const nums = readOriginalProbs(record);
    if (!nums || !labelName) return null;
    const sortedLabels = [...(allLabelOptions || [])].sort();
    if (sortedLabels.length !== nums.length) return null;
    const idx = sortedLabels.indexOf(labelName);
    return idx >= 0 ? nums[idx] : null;
}

/**
 * AI confidence for the Data Editor tooltip, in priority order:
 * 1. this tag's `original_probs` entry (label-only logprobs / prompt distribution)
 * 2. max of `original_probs` when the named label cannot be mapped
 * 3. mean token probability from `logprobs` (full-sample native logprobs)
 * 4. `response.uncalibrated_confidence` on fully-generated rows only
 *    (gateway self-reported confidence when native token logprobs are absent).
 * Label-only `uncalibrated_confidence` / `probs` often collapse to 1.0 and
 * must not be used as a fallback.
 */
function readAiConfidence(record, labelName, allLabelOptions) {
    const forLabel = readOriginalProbForLabel(record, labelName, allLabelOptions);
    if (forLabel != null) return forLabel;
    const nums = readOriginalProbs(record);
    if (nums) return Math.max(...nums);
    const fromLogprobs = confidenceFromLogprobs(record?.logprobs);
    if (fromLogprobs != null) return fromLogprobs;
    if (isFullyGeneratedRecord(record)) return readUncalibratedConfidence(record);
    return null;
}

/**
 * The label the AI originally predicted for this record: the argmax of
 * `original_probs`, which the generation handler aligns with the task's
 * label names sorted alphabetically. Returns null when the distribution is
 * missing or cannot be mapped onto the provided label set.
 */
export function getAiPredictedLabel(record, allLabelOptions) {
    const nums = readOriginalProbs(record);
    if (!nums) return null;
    const sortedLabels = [...(allLabelOptions || [])].sort();
    if (sortedLabels.length !== nums.length) return null;
    let bestIdx = 0;
    for (let i = 1; i < nums.length; i += 1) {
        if (nums[i] > nums[bestIdx]) bestIdx = i;
    }
    return sortedLabels[bestIdx];
}

/**
 * True when a human replaced the AI's predicted label with a different one.
 * Undetectable (false) when the record has no usable `original_probs`.
 */
export function isAiLabelOverridden(record, allLabelOptions, labelName) {
    if (!labelName || labelName === NONE_SELECTED) return false;
    const aiLabel = getAiPredictedLabel(record, allLabelOptions);
    return aiLabel != null && labelName !== aiLabel;
}

function isRecordReviewed(record) {
    return Boolean(record?.is_document_reviewed || record?.is_validated);
}

/**
 * Tooltip for a label tag in the Data Editor. For AI-labeled records the
 * text follows the review lifecycle:
 * - human replaced the AI label            -> human-annotation copy
 *   (single-label only; multi-label has no per-tag original set)
 * - label matches the AI prediction:
 *   - reviewed (human confirmed the AI)    -> human copy + AI confidence
 *   - not reviewed                         -> AI confidence only
 * - no per-record confidence data          -> no tooltip (or human copy once reviewed)
 */
export function getLabelConfidenceTooltip({
    record,
    isAiGenerated,
    labelName,
    allLabelOptions = [],
    isMultilabel = false,
}) {
    if (!labelName || labelName === NONE_SELECTED) return '';
    const overridden = !isMultilabel && isAiLabelOverridden(record, allLabelOptions, labelName);
    if (!isAiGenerated || overridden) {
        return HUMAN_LABEL_CONFIDENCE_TOOLTIP;
    }

    const pct = confidenceToPercent(readAiConfidence(record, labelName, allLabelOptions));
    if (isRecordReviewed(record)) {
        return pct == null
            ? HUMAN_LABEL_CONFIDENCE_TOOLTIP
            : `${HUMAN_LABEL_CONFIDENCE_TOOLTIP} (and ${pct}% AI confidence prediction)`;
    }
    return pct == null ? '' : `${pct}% AI confidence prediction`;
}
